import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SafetyBanner } from "../components/SafetyBanner";
import { Reveal } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_HREF } from "../lib/site";

const MaskedLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  </div>
);

const CHAPTERS = [
  {
    num: "01",
    title: "The Short Version",
    body: "Minneapolis has plenty of roadside providers. Too many quote $49 to get you interested, then stack on night fees, weekend fees, and 'distance' fees until the bill doubles. Tru North Towing and Recovery was built to be the opposite of that phone call: one flat price, quoted before we roll, honored when we arrive.",
  },
  {
    num: "02",
    title: "Why Mobile-Only",
    body: "No tow yard. No lot. No overhead. One fully stocked service van that comes to wherever you're stranded — a downtown ramp, a snowy shoulder on 35W, your own driveway. That's exactly how a jump start stays $90 at 3 AM on a Sunday.",
  },
  {
    num: "03",
    title: "The Name",
    body: "True north is the compass point that never moves. That's the standard here: pricing that doesn't change after dark, a local technician who answers his own phone, and work done like your car is ours. If we can't fix it roadside, we'll tell you straight — no charge, no runaround.",
  },
];

const STATS = [
  ["24/7 / 365", "Nights, weekends, holidays"],
  ["7 Zones", "Minneapolis + the metro"],
  ["~60 Cities", "And growing with demand"],
  ["$0", "After-hours surcharge, ever"],
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Navbar />

      <section className="relative pt-36 pb-20 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 -z-10">
          <img src="/images/flatbed.png" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }} className="eyebrow mb-6">
            About Tru North — Minneapolis, MN
          </motion.p>
          <h1 className="font-display font-bold uppercase tracking-normal leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-center">
            <MaskedLine delay={0.1}>One van.</MaskedLine>
            <MaskedLine delay={0.25}>One technician.</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-primary">Zero games.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 max-w-xl text-neutral-300 text-base md:text-lg"
          >
            Tru North Towing and Recovery is a 24/7 mobile-only roadside assistance company. Jump starts, tire changes, lockouts, fuel delivery, and battery replacement — at a price you already know.
          </motion.p>
        </div>
      </section>

      <SafetyBanner />

      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-14 items-start">
          <div>
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div className="grid md:grid-cols-[110px_1fr] gap-4 md:gap-8 py-10 border-t border-white/10 first:border-t-0 first:pt-0">
                  <span className="font-mono text-primary text-3xl font-semibold">{c.num}</span>
                  <div>
                    <h2 className="font-display font-semibold uppercase tracking-normal text-2xl md:text-3xl">{c.title}</h2>
                    <p className="mt-4 text-neutral-400 text-base leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="lg:sticky lg:top-24">
            <div className="relative">
              <div className="absolute -inset-6 bg-primary/20 blur-3xl rounded-full -z-10" />
              <div className="border border-white/10 overflow-hidden">
                <img src="/images/van.png" alt="Tru North service van at night" className="w-full aspect-[4/3] object-cover" />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3" data-testid="about-stats">
                {STATS.map(([big, small]) => (
                  <div key={small} className="border border-white/10 bg-[#0D0D0D] p-4">
                    <p className="font-display font-bold text-2xl text-primary">{big}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">{small}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-white/10 bg-[#080808]" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center gap-8 justify-between">
          <Reveal>
            <h2 className="font-display font-bold uppercase tracking-normal text-4xl sm:text-5xl leading-[1.02]">
              Stranded right now?
            </h2>
            <p className="mt-3 text-neutral-400 max-w-md">Skip the form, skip the wait — the dispatch line is answered around the clock.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <a data-testid="about-call-btn" href={PHONE_HREF} className="btn-primary">
                <Phone size={16} /> {PHONE_DISPLAY}
              </a>
              <a data-testid="about-pricing-btn" href="/pricing" className="btn-ghost">
                See Upfront Pricing
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
